import { type FC, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Form,
  type FormErrors,
  type InjectedFormProps,
  SubmissionError,
  formValueSelector,
  reduxForm,
} from "redux-form";

import { Dialog } from "widgets";
import { useControlledDialog } from "widgets/hooks";

import { handleTabInForm, simpleDropdownFieldValidation, simpleFieldValidation } from "../../../app-utils/form.js";
import { type Template } from "../../../graphql.js";
import { type IAppState } from "../../../redux/reducer.js";
import CheckboxField, { CheckboxReduxFormField } from "../redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../redux-form/ReactSelectField.js";

interface SaveTemplateFormProps extends InjectedFormProps<unknown, unknown> {
  columnTemplates: Template[];
  onClose: () => void;
  saveNewTemplate: (name: string) => Promise<Template>;
  updateExistingTemplate: (columnTemplate: Template) => Promise<Template>;
}

const FORM_NAME = "SAVE_TEMPLATE";
const EXISTING_TEMPLATE_CHECKBOX_FIELD_NAME = "EXISTING_TEMPLATE_CHECKBOX";
const EXISTING_TEMPLATE_FIELD_NAME = "EXISTING_TEMPLATE";
const NEW_TEMPLATE_FIELD_NAME = "NEW_TEMPLATE";

const SaveTemplateForm: FC<SaveTemplateFormProps> = (props) => {
  const recycle = useSelector<IAppState>((state) =>
    formValueSelector(FORM_NAME)(state, EXISTING_TEMPLATE_CHECKBOX_FIELD_NAME),
  );

  const handleSubmitData = useCallback(
    (values: unknown) => {
      const existingTemplateColumn: Template = values[EXISTING_TEMPLATE_FIELD_NAME];
      const newColumnTemplateName: string = values[NEW_TEMPLATE_FIELD_NAME];

      const promise: Promise<Template> = recycle
        ? props.updateExistingTemplate(existingTemplateColumn)
        : props.saveNewTemplate(newColumnTemplateName);
      return promise.then(
        (fulfilled) => {
          props.onClose();
          return fulfilled;
        },
        (rejected) => {
          // show the error directly in the dialog
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [props],
  );

  const { dialogRef, dialogProps, state, underlayProps } = useControlledDialog(props.onClose);

  return state.isOpen ? (
    <Dialog
      title="Save as Template"
      ref={dialogRef}
      domProps={dialogProps}
      underlayProps={underlayProps}
      mainButton={{
        label: recycle ? "Update Template" : "Save Template",
        isLoading: props.submitting,
        onPress: props.handleSubmit(handleSubmitData),
      }}
      closeFn={props.onClose}
      errorCallout={props.error ? { message: props.error } : undefined}
      width={320}
    >
      <Form onSubmit={props.handleSubmit(handleSubmitData)} onKeyDownCapture={handleTabInForm}>
        <CheckboxReduxFormField
          horizontalLayout={false}
          key={`save-template-recycle-checkbox`}
          name={EXISTING_TEMPLATE_CHECKBOX_FIELD_NAME}
          label={"Update existing template"}
          tooltipText={`Use an existing template or create a new one`}
          component={CheckboxField}
        />

        {recycle ? (
          <ReactSelectReduxFormField
            horizontalLayout={false}
            key={`update-existing-template`}
            name={EXISTING_TEMPLATE_FIELD_NAME}
            isClearable={false}
            isSearchable={true}
            label={"Existing template"}
            tooltip={`Please select a template to update`}
            placeholder={"Please select..."}
            component={ReactSelectField}
            validate={simpleDropdownFieldValidation}
            isMulti={false}
            options={props.columnTemplates.filter((template: Template) => !template.default && !template.active)}
            getOptionValue={(option: Template) => option.id}
            getOptionLabel={(option: Template) => option.name}
            closeMenuOnSelect={true}
            openMenuOnFocus={true}
            menuPosition={"absolute"}
          />
        ) : (
          <InputReduxFormField
            key={`enter-new-name-template`}
            name={NEW_TEMPLATE_FIELD_NAME}
            componentClass="input"
            label={"Template name"}
            placeholder="Please choose a name"
            component={InputField}
            validate={simpleFieldValidation}
          />
        )}
      </Form>
    </Dialog>
  ) : null;
};

const validate = (...params: unknown[]): FormErrors<unknown> => {
  const values = params[0]; // because the typescript definition is not up to date !
  const props = params[1]; // because the typescript definition is not up to date !

  if (props && !props.recycle) {
    const newTemplateName: string = values[NEW_TEMPLATE_FIELD_NAME];
    if (props.columnTemplates?.find((ct: Template) => ct.name === newTemplateName)) {
      return { [NEW_TEMPLATE_FIELD_NAME]: "This template name already exists." };
    }
  }
  return {};
};

export const SaveTemplateControlledDialog = reduxForm({ form: FORM_NAME, validate })(SaveTemplateForm);
